import { useState } from "react";
import { Maximize2, MapPin, Sprout } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import Section from "../components/Section";
import Badge from "../components/Badge";
import Seo from "../components/Seo";
import CrecheMap from "../components/CrecheMap";
import { fadeUp, springSoft, staggerContainer, viewportOnce } from "../lib/motion";
import { creches, libelleExterieur } from "../data/creches";
import { breadcrumbJsonLd, crechesItemListJsonLd } from "../lib/jsonld";
import { site } from "../data/site";

const MotionLink = motion.create(Link);

export default function Creches() {
  const [highlightedSlug, setHighlightedSlug] = useState<string | undefined>();

  return (
    <>
      <Seo
        path="/nos-micro-creches"
        title="Nos micro-crèches en Normandie"
        description="Les micro-crèches La La Land en Seine-Maritime et Eure : Isneauville, Notre-Dame-de-Bondeville, Saint-Étienne-du-Rouvray, Mont-Saint-Aignan, Vernon, Saint-Marcel, Rouen Beauvoisine."
        jsonLd={[
          breadcrumbJsonLd([
            { name: "Accueil", url: site.url },
            { name: "Nos micro-crèches", url: `${site.url}/nos-micro-creches` },
          ]),
          crechesItemListJsonLd(creches),
        ]}
      />
      <Section
        eyebrow="Nos implantations"
        title="Nos micro-crèches en Normandie"
        description="Chacune avec son équipe, son ambiance et son projet musical. Cliquez sur un repère pour découvrir la crèche."
      >
        <div className="space-y-8 lg:space-y-0 lg:grid lg:grid-cols-5 lg:gap-8 lg:items-start">
          <div className="lg:col-span-2 lg:order-2 lg:sticky lg:top-28">
            <CrecheMap creches={creches} highlightedSlug={highlightedSlug} />
          </div>

          <motion.div
            className="lg:col-span-3 grid sm:grid-cols-2 gap-6"
            variants={staggerContainer()}
            initial="hidden"
            whileInView="show"
            viewport={viewportOnce}
          >
            {creches.map((c) => (
              <MotionLink
                key={c.slug}
                to={`/micro-creches/${c.slug}`}
                variants={fadeUp}
                whileHover={{ y: -6 }}
                transition={springSoft}
                onMouseEnter={() => setHighlightedSlug(c.slug)}
                onMouseLeave={() => setHighlightedSlug(undefined)}
                onFocus={() => setHighlightedSlug(c.slug)}
                onBlur={() => setHighlightedSlug(undefined)}
                className={`group bg-surface border rounded-3xl overflow-hidden shadow-soft-sm hover:shadow-soft-lg transition ${
                  highlightedSlug === c.slug
                    ? "border-sage ring-2 ring-sage/30"
                    : "border-edge-soft"
                }`}
              >
                {c.photosDisponibles ? (
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={c.photoPrincipale}
                      alt={c.nom}
                      className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="aspect-[4/3] bg-gradient-to-br from-sage-soft via-honey-soft to-terra-soft flex items-end p-4">
                    <Badge tone="neutral" className="bg-white/85 backdrop-blur">
                      Photo · à fournir
                    </Badge>
                  </div>
                )}
                <div className="p-5 md:p-6 space-y-3">
                  <h3 className="font-display text-xl font-semibold tracking-tight group-hover:text-terra transition-colors">
                    {c.nom}
                  </h3>
                  <p className="text-sm text-ink-soft inline-flex items-start gap-1.5">
                    <MapPin size={14} strokeWidth={1.8} className="mt-0.5 shrink-0 text-ink-muted" />
                    <span>
                      {c.adresse} · {c.codePostal} {c.ville}
                    </span>
                  </p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Badge tone="musical" className="!text-[13px] !py-1 !px-3 font-semibold">
                      <Maximize2 size={12} strokeWidth={2.2} />
                      {c.superficie} m²
                    </Badge>
                    {c.superficieJardin > 0 ? (
                      <Badge tone="warm" className="!text-[13px] !py-1 !px-3 font-semibold !text-ink">
                        <Sprout size={13} strokeWidth={2.2} className="text-honey-hover" />
                        {libelleExterieur(c)} {c.superficieJardin} m²
                      </Badge>
                    ) : null}
                  </div>
                </div>
              </MotionLink>
            ))}
          </motion.div>
        </div>
      </Section>
    </>
  );
}
